<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import BreadcrumbTitle from 'dashboard/components-next/breadcrumb/BreadcrumbTitle.vue';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import { useOperators } from 'dashboard/components-next/filter/operators';
import {
  buildPricingConditionFilterTypes,
  normalizeConditionValueForEditor,
  serializeConditionValues,
} from './pricingConditions';
import FieldsetSection from './FieldsetSection.vue';
import ItemDialog from './ItemDialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import EventsAPI from 'dashboard/api/planning/events';
import { usePricingRulesStore } from 'dashboard/stores/pricingRules';
import {
  isReadOnlyPricingPlan,
  isIntegrationPricingPlan,
} from 'dashboard/composables/useShijiBookingLink';

const store = useStore();
const pricingRulesStore = usePricingRulesStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const alertMessage = useAlert;
const {
  equalityOperators,
  comparisonOperators,
  containmentOperators,
  pricingDateOperators,
} = useOperators();

const planId = computed(() => Number(route.params.planId));
const ruleId = computed(() => {
  const id = route.params.ruleId;
  return id && id !== 'new' ? Number(id) : null;
});
const isEditing = computed(() => !!ruleId.value);

const plan = computed(
  () => store.getters['pricingPlans/getPlanById'](planId.value) || {}
);
const isReadOnlyPlan = computed(() => isReadOnlyPricingPlan(plan.value));
const isIntegrationPlan = computed(() => isIntegrationPricingPlan(plan.value));
const readOnlyPlanMessage = computed(() => {
  if (isIntegrationPlan.value) return t('PRICING.PLANS.MANAGED_BY_INTEGRATION');
  if (plan.value.experience_id) return t('PRICING.PLANS.MANAGED_BY_EXPERIENCE');
  return '';
});
const items = computed(() => store.getters['pricingItems/getRecords'] || []);

const loading = ref(true);
const saving = ref(false);
let loadRequestId = 0;

const defaultFormState = () => ({
  pricing_item_id: '',
  priority: 0,
  clauses: [],
});

// Form state
const form = ref(defaultFormState());

// New-price dialog state
const itemDialogRef = ref(null);
const pendingPriceClauseIdx = ref(null);

const openNewPriceDialog = clauseIdx => {
  pendingPriceClauseIdx.value = clauseIdx;
  itemDialogRef.value?.open();
};

const handleNewPriceSaved = async data => {
  if (!selectedItem.value?.id) return;
  try {
    const updated = await store.dispatch('pricingItems/update', {
      id: selectedItem.value.id,
      ...data,
    });
    // Find the newly created price (the one not in previous prices)
    const oldIds = new Set(itemPrices.value.map(p => p.id));
    const newPrice = (updated.prices || []).find(p => !oldIds.has(p.id));
    if (newPrice && pendingPriceClauseIdx.value !== null) {
      const clause =
        pendingPriceClauseIdx.value === 'default'
          ? defaultClause.value
          : conditionalClauses.value[pendingPriceClauseIdx.value];
      if (clause) {
        clause.pricing_item_price_id = newPrice.id;
      }
    }
    pendingPriceClauseIdx.value = null;
  } catch {
    alertMessage(t('PRICING.MASTER_SHEET.SAVE_ERROR'));
  }
};

// Load stage options for conditions
const stageOptions = ref([]);
const loadStageOptions = async () => {
  try {
    const response = await EventsAPI.statusOptions();
    const system = (response.data.system_statuses || []).map(s => ({
      id: s.system_stage,
      name: s.label,
    }));
    const custom = (response.data.custom_statuses || []).map(s => ({
      id: s.key,
      name: s.label,
    }));
    stageOptions.value = [...system, ...custom];
  } catch {
    stageOptions.value = [];
  }
};

const dayOfWeekOptions = [
  { id: 'monday', name: t('PRICING.DAYS.MONDAY') },
  { id: 'tuesday', name: t('PRICING.DAYS.TUESDAY') },
  { id: 'wednesday', name: t('PRICING.DAYS.WEDNESDAY') },
  { id: 'thursday', name: t('PRICING.DAYS.THURSDAY') },
  { id: 'friday', name: t('PRICING.DAYS.FRIDAY') },
  { id: 'saturday', name: t('PRICING.DAYS.SATURDAY') },
  { id: 'sunday', name: t('PRICING.DAYS.SUNDAY') },
];

const eventLabelOptions = computed(() =>
  (store.getters['planningEventLabels/getRecords'] || []).map(label => ({
    id: label.id,
    name: label.title,
  }))
);

const derivedAttrLabels = computed(() => ({
  systemStage: t('PRICING.DERIVED_ATTRS.SYSTEM_STAGE'),
  eventLabel: t('PRICING.DERIVED_ATTRS.EVENT_LABEL'),
  dayOfWeek: t('PRICING.DERIVED_ATTRS.DAY_OF_WEEK'),
  startDate: t('PRICING.DERIVED_ATTRS.START_DATE'),
  durationHours: t('PRICING.DERIVED_ATTRS.DURATION_HOURS'),
  numberOfDays: t('PRICING.DERIVED_ATTRS.NUMBER_OF_DAYS'),
  pax: t('PRICING.DERIVED_ATTRS.PAX'),
}));

const conditionFilterTypes = computed(() => {
  const eventAttrs =
    store.getters['attributes/getAttributesByModel']('event_attribute') || [];
  const agentOptions = (store.getters['agents/getAgents'] || []).map(agent => ({
    id: agent.id,
    name: agent.name,
  }));
  return buildPricingConditionFilterTypes(
    eventAttrs,
    stageOptions.value,
    dayOfWeekOptions,
    {
      equalityOperators: equalityOperators.value,
      comparisonOperators: comparisonOperators.value,
      containmentOperators: containmentOperators.value,
      dateOperators: pricingDateOperators.value,
    },
    agentOptions,
    eventLabelOptions.value,
    derivedAttrLabels.value
  );
});

// Selected pricing item and its prices
const selectedItem = computed(() =>
  items.value.find(i => String(i.id) === String(form.value.pricing_item_id))
);
const itemPrices = computed(() => selectedItem.value?.prices || []);
const priceOptions = computed(() =>
  itemPrices.value.map(p => ({
    value: p.id,
    label: `${selectedItem.value?.name || ''} - ${p.name || t(`PRICING.UNITS.${p.unit.toUpperCase()}`)} (${p.price} ${p.currency})`,
  }))
);

// Items dropdown options (only items with prices that don't already have a rule in this plan)
const usedItemIds = computed(
  () => new Set(pricingRulesStore.records.map(r => r.pricing_item_id))
);
const eventAttributes = computed(
  () =>
    store.getters['attributes/getAttributesByModel']?.('event_attribute') || []
);
const validAttributeKeys = computed(
  () => new Set(eventAttributes.value.map(a => a.attribute_key))
);
const itemOptions = computed(() =>
  items.value
    .filter(
      i =>
        i.prices?.length > 0 &&
        !usedItemIds.value.has(i.id) &&
        (!i.attribute_key || validAttributeKeys.value.has(i.attribute_key))
    )
    .map(i => ({ value: String(i.id), label: i.name }))
);

// Clause helpers
const defaultClause = computed(
  () => form.value.clauses.find(c => c.is_default) || form.value.clauses[0]
);
const conditionalClauses = computed(() =>
  form.value.clauses.filter(c => !c.is_default)
);

const addClause = () => {
  const firstAttr = conditionFilterTypes.value[0];
  form.value.clauses.push({
    conditions: [
      {
        attribute_key: firstAttr?.attributeKey || '',
        filter_operator: firstAttr?.filterOperators?.[0]?.value || 'equal_to',
        values: '',
        query_operator: 'and',
      },
    ],
    pricing_item_price_id: itemPrices.value[0]?.id || null,
    is_default: false,
    position: form.value.clauses.length,
  });
};

const removeClause = idx => {
  const clause = conditionalClauses.value[idx];
  const realIdx = form.value.clauses.indexOf(clause);
  if (realIdx > -1) form.value.clauses.splice(realIdx, 1);
};

const addConditionToClause = clauseIdx => {
  const clause = conditionalClauses.value[clauseIdx];
  const firstAttr = conditionFilterTypes.value[0];
  clause.conditions.push({
    attribute_key: firstAttr?.attributeKey || '',
    filter_operator: firstAttr?.filterOperators?.[0]?.value || 'equal_to',
    values: '',
    query_operator: 'and',
  });
};

const removeConditionFromClause = (clauseIdx, condIdx) => {
  const clause = conditionalClauses.value[clauseIdx];
  clause.conditions.splice(condIdx, 1);
  if (clause.conditions.length === 0) {
    removeClause(clauseIdx);
  }
};

// Auto-select the default price when item changes (only for new rules)
watch(
  () => form.value.pricing_item_id,
  () => {
    if (isEditing.value) return;
    const defaultPrice = itemPrices.value.find(p => p.is_default);
    const priceId = defaultPrice?.id || itemPrices.value[0]?.id || null;
    const defClause = form.value.clauses.find(c => c.is_default);
    if (defClause) defClause.pricing_item_price_id = priceId;
  }
);

// Breadcrumb
const breadcrumbItems = computed(() => {
  const crumbs = [
    { label: t('PRICING.PLANS.TITLE'), routeName: 'settings_pricing_plans' },
    {
      label: plan.value.name || '...',
      routeName: 'settings_pricing_plan_show',
    },
  ];
  if (selectedItem.value) {
    crumbs.push({ label: selectedItem.value.name });
  } else if (!isEditing.value) {
    crumbs.push({ label: t('PRICING.RULES.ADD_RULE') });
  }
  return crumbs;
});

const handleBreadcrumbClick = item => {
  if (item.routeName === 'settings_pricing_plans') {
    router.push({ name: 'settings_pricing_plans' });
  } else if (item.routeName === 'settings_pricing_plan_show') {
    router.push({
      name: 'settings_pricing_plan_show',
      params: { planId: planId.value },
    });
  }
};

// Validation
const isValid = computed(() => {
  if (!form.value.pricing_item_id) return false;
  if (!defaultClause.value?.pricing_item_price_id) return false;
  return conditionalClauses.value.every(
    c => c.conditions.length > 0 && c.pricing_item_price_id
  );
});

// Initialize form from existing rule
const initFormFromRule = rule => {
  form.value.pricing_item_id = String(rule.pricing_item_id || '');
  form.value.priority = rule.priority ?? 0;

  if (rule.clauses?.length) {
    form.value.clauses = rule.clauses.map(c => ({
      conditions: (c.conditions || []).map(cond => {
        const filterType = conditionFilterTypes.value.find(
          f => f.attributeKey === cond.attribute_key
        );
        return {
          ...cond,
          values: normalizeConditionValueForEditor(cond.values, filterType),
        };
      }),
      pricing_item_price_id: c.pricing_item_price_id,
      is_default: !!c.is_default,
      position: c.position || 0,
    }));
  } else {
    form.value.clauses = [
      {
        conditions: [],
        pricing_item_price_id: null,
        is_default: true,
        position: 0,
      },
    ];
  }
};

const initFormForNewRule = () => {
  const itemId = route.query.itemId;
  form.value = {
    pricing_item_id: itemId ? String(itemId) : '',
    priority: 0,
    clauses: [
      {
        conditions: [],
        pricing_item_price_id: null,
        is_default: true,
        position: 0,
      },
    ],
  };
};

const loadEditor = async () => {
  const currentRequestId = loadRequestId + 1;
  loadRequestId = currentRequestId;
  loading.value = true;
  form.value = defaultFormState();
  pendingPriceClauseIdx.value = null;

  try {
    await Promise.all([
      store.dispatch('pricingPlans/show', planId.value),
      pricingRulesStore.get(planId.value),
      items.value.length ? null : store.dispatch('pricingItems/get'),
      store.dispatch('attributes/get'),
      store.dispatch('planningEventLabels/get'),
      loadStageOptions(),
    ]);

    if (currentRequestId !== loadRequestId) return;

    if (isEditing.value) {
      const rule = pricingRulesStore.getRuleById(ruleId.value);
      if (rule) {
        initFormFromRule(rule);
      } else {
        router.replace({
          name: 'settings_pricing_plan_show',
          params: { planId: planId.value },
        });
      }
    } else {
      initFormForNewRule();
    }
  } finally {
    if (currentRequestId === loadRequestId) {
      loading.value = false;
    }
  }
};

// Save
const handleSave = async () => {
  if (!isValid.value || saving.value) return;
  saving.value = true;

  const clauses = form.value.clauses.map((c, idx) => ({
    conditions: c.is_default
      ? []
      : c.conditions.map(cond => ({
          attribute_key: cond.attribute_key,
          filter_operator: cond.filter_operator,
          values: serializeConditionValues(cond.values),
          query_operator: cond.query_operator || 'and',
        })),
    pricing_item_price_id: c.pricing_item_price_id,
    is_default: c.is_default,
    position: idx,
  }));

  const payload = {
    pricing_item_id: Number(form.value.pricing_item_id),
    clauses,
    priority: form.value.priority,
  };

  try {
    if (isEditing.value) {
      await pricingRulesStore.update({
        planId: planId.value,
        id: ruleId.value,
        ...payload,
      });
      alertMessage(t('PRICING.RULES.UPDATE_SUCCESS'));
    } else {
      await pricingRulesStore.create({
        planId: planId.value,
        ...payload,
      });
      alertMessage(t('PRICING.RULES.CREATE_SUCCESS'));
    }
    router.push({
      name: 'settings_pricing_plan_show',
      params: { planId: planId.value },
    });
  } catch {
    alertMessage(t('PRICING.RULES.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const handleCancel = () => {
  router.push({
    name: 'settings_pricing_plan_show',
    params: { planId: planId.value },
  });
};

watch(
  [planId, ruleId, () => route.query.itemId],
  () => {
    loadEditor();
  },
  { immediate: true }
);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('PRICING.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BreadcrumbTitle
        :items="breadcrumbItems"
        :description="t('PRICING.RULE_EDITOR.DESCRIPTION')"
        @click="handleBreadcrumbClick"
      />
    </template>

    <template #body>
      <div class="w-full space-y-6">
        <div
          v-if="isReadOnlyPlan"
          class="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-n-amber-6 bg-n-amber-3 text-n-amber-11 text-sm"
        >
          <Icon icon="i-lucide-lock" class="size-4 shrink-0" />
          <span>{{ readOnlyPlanMessage }}</span>
        </div>

        <!-- Pricing item selector (only for new rules) -->
        <div v-if="!isEditing && !isReadOnlyPlan">
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('PRICING.RULE_DIALOG.ITEM_LABEL') }}
          </label>
          <select
            v-model="form.pricing_item_id"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
          >
            <option value="" disabled>
              {{ t('PRICING.RULE_DIALOG.ITEM_HINT') }}
            </option>
            <option
              v-for="opt in itemOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Pricing item display (read-only when editing) -->
        <div v-else-if="selectedItem">
          <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
            {{ t('PRICING.RULE_DIALOG.ITEM_LABEL') }}
          </label>
          <div
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          >
            {{ selectedItem.name }}
          </div>
        </div>

        <!-- Conditional clauses -->
        <template v-if="itemPrices.length">
          <FieldsetSection
            v-for="(clause, cIdx) in conditionalClauses"
            :key="cIdx"
            :legend="
              cIdx === 0
                ? t('PRICING.RULE_EDITOR.WHEN_THIS_OCCURS')
                : t('PRICING.RULE_EDITOR.OR_WHEN_THIS_OCCURS')
            "
          >
            <div class="space-y-3">
              <ConditionRow
                v-for="(cond, condIdx) in clause.conditions"
                :key="condIdx"
                v-model:attribute-key="cond.attribute_key"
                v-model:filter-operator="cond.filter_operator"
                v-model:values="cond.values"
                v-model:query-operator="cond.query_operator"
                :show-query-operator="condIdx > 0"
                :filter-types="conditionFilterTypes"
                :read-only="isReadOnlyPlan"
                @remove="removeConditionFromClause(cIdx, condIdx)"
              />

              <button
                v-if="!isReadOnlyPlan"
                class="flex items-center gap-1.5 text-xs font-medium text-n-brand hover:text-n-brand-dark"
                @click="addConditionToClause(cIdx)"
              >
                <Icon icon="i-lucide-plus" class="size-3.5" />
                {{ t('PRICING.RULE_DIALOG.ADD_CONDITION') }}
              </button>
            </div>

            <div class="mt-4">
              <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                {{ t('PRICING.RULE_EDITOR.APPLY_PRICE_VARIATION') }}
              </label>
              <ComboBox
                :model-value="clause.pricing_item_price_id"
                :options="priceOptions"
                :placeholder="t('PRICING.RULE_EDITOR.SELECT_PRICE')"
                :disabled="isReadOnlyPlan"
                class="max-w-sm"
                @update:model-value="v => (clause.pricing_item_price_id = v)"
              >
                <template v-if="!isReadOnlyPlan" #footer>
                  <button
                    type="button"
                    class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-2 rounded-md transition-colors"
                    @click.stop="openNewPriceDialog(cIdx)"
                  >
                    <span class="i-lucide-plus size-3.5" />
                    {{ t('PRICING.MASTER_SHEET.ADD_PRICE') }}
                  </button>
                </template>
              </ComboBox>
            </div>

            <div v-if="!isReadOnlyPlan" class="mt-3 flex justify-end">
              <Button
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                @click="removeClause(cIdx)"
              />
            </div>
          </FieldsetSection>

          <!-- Add rule button -->
          <Button
            v-if="!isReadOnlyPlan"
            md
            slate
            faded
            icon="i-lucide-plus"
            class="w-full"
            :label="t('PRICING.RULE_EDITOR.ADD_RULE')"
            @click="addClause"
          />

          <!-- Otherwise apply (default clause) -->
          <FieldsetSection
            v-if="defaultClause"
            :legend="t('PRICING.RULE_EDITOR.OTHERWISE_APPLY')"
          >
            <ComboBox
              :model-value="defaultClause.pricing_item_price_id"
              :options="priceOptions"
              :placeholder="t('PRICING.RULE_EDITOR.SELECT_PRICE')"
              :disabled="isReadOnlyPlan"
              class="max-w-sm"
              @update:model-value="
                v => (defaultClause.pricing_item_price_id = v)
              "
            >
              <template v-if="!isReadOnlyPlan" #footer>
                <button
                  type="button"
                  class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-2 rounded-md transition-colors"
                  @click.stop="openNewPriceDialog('default')"
                >
                  <span class="i-lucide-plus size-3.5" />
                  {{ t('PRICING.MASTER_SHEET.ADD_PRICE') }}
                </button>
              </template>
            </ComboBox>
          </FieldsetSection>
        </template>

        <!-- Footer actions -->
        <div
          class="flex items-center justify-end gap-3 pt-4 border-t border-n-weak"
        >
          <Button
            md
            slate
            faded
            :label="t('PRICING.RULE_EDITOR.CANCEL')"
            @click="handleCancel"
          />
          <Button
            v-if="!isReadOnlyPlan"
            md
            :label="t('PRICING.RULE_EDITOR.SAVE')"
            :disabled="!isValid || saving"
            @click="handleSave"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>

  <ItemDialog
    ref="itemDialogRef"
    :row="
      selectedItem
        ? { name: selectedItem.name, pricingItem: selectedItem }
        : null
    "
    :price="null"
    @save="handleNewPriceSaved"
    @close="pendingPriceClauseIdx = null"
  />
</template>
